'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function EstateTaxCalculator() {
  const t = useTranslations("tools.estateTaxCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    residence: 0,
    stock: 0,
    saving: 0,
    vehicle: 0,
    retirement: 0,
    lifeinsurance: 0,
    otherasset: 0,
    debt: 0,
    funeral: 0,
    charitable: 0,
    statetax: 0,
    lifetimegifted: 0,
  });

  const [results, setResults] = useState<any>(null);

  const EXEMPTION_2025 = 13990000;
  const TAX_RATE = 0.40;

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleInputChange = (field: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setInputs(prev => ({ ...prev, [field]: Math.max(0, numValue) }));
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const grossEstate = inputs.residence + inputs.stock + inputs.saving +
                       inputs.vehicle + inputs.retirement + inputs.lifeinsurance +
                       inputs.otherasset;

    const totalDeductions = inputs.debt + inputs.funeral + inputs.charitable + inputs.statetax;

    const adjustedGrossEstate = Math.max(0, grossEstate - totalDeductions);

    const taxableEstate = adjustedGrossEstate + inputs.lifetimegifted;

    const taxableAmount = Math.max(0, taxableEstate - EXEMPTION_2025);

    const estateTax = taxableAmount * TAX_RATE;

    setResults({
      grossEstate,
      totalDeductions,
      adjustedGrossEstate,
      lifetimegifted: inputs.lifetimegifted,
      taxableEstate,
      exemption: EXEMPTION_2025,
      taxableAmount,
      estateTax,
    });

    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }, 100);
  };

  const handleClear = () => {
    setInputs({
      residence: 0,
      stock: 0,
      saving: 0,
      vehicle: 0,
      retirement: 0,
      lifeinsurance: 0,
      otherasset: 0,
      debt: 0,
      funeral: 0,
      charitable: 0,
      statetax: 0,
      lifetimegifted: 0,
    });
    setResults(null);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '100%',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
    },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: '2rem',
    textAlign: 'center' as const,
  };

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const sectionTitleStyle = {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: 'white',
    padding: '0.75rem 1rem',
    margin: screenSize === 'mobile' ? '0 -1.5rem 1.5rem -1.5rem' : '0 -2rem 1.5rem -2rem',
    fontSize: '1.1rem',
    fontWeight: 600,
  };

  const formGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1.25rem',
      marginBottom: '2rem',
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
  };

  const labelStyle = {
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '0.5rem',
    fontSize: '0.95rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      outline: 'none',
    },
    {},
    {},
    {}
  );

  const buttonGroupStyle = getResponsiveStyle(
    {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      marginTop: '2rem',
    },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      padding: '0.875rem 2.5rem',
      fontSize: '1.05rem',
      fontWeight: 600,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
    },
    { width: '100%' },
    { width: 'auto' },
    { width: 'auto' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      padding: '0.875rem 2.5rem',
      fontSize: '1.05rem',
      fontWeight: 600,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
    },
    { width: '100%' },
    { width: 'auto' },
    { width: 'auto' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      border: '2px solid #86efac',
    },
    { margin: '1.5rem', padding: '1.5rem' },
    { margin: '2rem', padding: '2rem' },
    { margin: '2rem', padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    {
      margin: '0 0 1.5rem 0',
      color: '#166534',
      textAlign: 'center' as const,
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = {
    display: 'grid',
    gap: '1rem',
  };

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    background: 'white',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #2563eb',
  };

  const resultLabelStyle = {
    fontWeight: 600,
    color: '#475569',
  };

  const resultValueStyle = getResponsiveStyle(
    {
      fontWeight: 700,
      color: '#2563eb',
    },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const resultHighlightStyle = {
    ...resultItemStyle,
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    borderLeft: '4px solid #f59e0b',
    padding: '1.25rem',
  };

  const resultHighlightValueStyle = getResponsiveStyle(
    {
      fontWeight: 700,
      color: '#d97706',
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const taxTableStyle = {
    marginTop: '2rem',
    padding: '1.5rem',
    background: 'white',
    borderRadius: '0.5rem',
  };

  const taxTableH4Style = {
    margin: '0 0 1rem 0',
    color: '#1e293b',
    fontSize: '1.1rem',
  };

  const taxBreakdownStyle = {
    display: 'grid',
    gap: '0.75rem',
  };

  const taxRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem',
    background: '#f8fafc',
    borderRadius: '0.375rem',
  };

  const taxRowHighlightStyle = {
    ...taxRowStyle,
    background: '#fef3c7',
    fontWeight: 600,
  };

  const infoBannerStyle = getResponsiveStyle(
    {
      padding: '1rem 1.5rem',
      background: '#eff6ff',
      borderLeft: '4px solid #2563eb',
      borderRadius: '0.5rem',
      color: '#1e40af',
      fontSize: '0.95rem',
    },
    { margin: '1.5rem' },
    { margin: '2rem' },
    { margin: '2rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem',
  };

  const listItemStyle = {
    marginBottom: '0.75rem',
  };

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = {
    ...h2Style,
    fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem',
    color: colors.primary,
    marginBottom: '0.5rem',
  };

  const referenceStyle = {
    marginBottom: '0.75rem',
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const,
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>{t("toolTitle")}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={sectionTitleStyle}>{t("assetsSection")}</div>
            <div style={formGridStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="residence">{t("residenceLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="residence"
                  value={inputs.residence}
                  onChange={(e) => handleInputChange('residence', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="stock">{t("stockLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="stock"
                  value={inputs.stock}
                  onChange={(e) => handleInputChange('stock', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="saving">{t("savingLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="saving"
                  value={inputs.saving}
                  onChange={(e) => handleInputChange('saving', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="vehicle">{t("vehicleLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="vehicle"
                  value={inputs.vehicle}
                  onChange={(e) => handleInputChange('vehicle', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="retirement">{t("retirementLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="retirement"
                  value={inputs.retirement}
                  onChange={(e) => handleInputChange('retirement', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="lifeinsurance">{t("lifeInsuranceLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="lifeinsurance"
                  value={inputs.lifeinsurance}
                  onChange={(e) => handleInputChange('lifeinsurance', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="otherasset">{t("otherAssetLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="otherasset"
                  value={inputs.otherasset}
                  onChange={(e) => handleInputChange('otherasset', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
            </div>

            <div style={sectionTitleStyle}>{t("liabilitySection")}</div>
            <div style={formGridStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="debt">{t("debtLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="debt"
                  value={inputs.debt}
                  onChange={(e) => handleInputChange('debt', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="funeral">{t("funeralLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="funeral"
                  value={inputs.funeral}
                  onChange={(e) => handleInputChange('funeral', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="charitable">{t("charitableLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="charitable"
                  value={inputs.charitable}
                  onChange={(e) => handleInputChange('charitable', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="statetax">{t("stateTaxLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="statetax"
                  value={inputs.statetax}
                  onChange={(e) => handleInputChange('statetax', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
            </div>

            <div style={sectionTitleStyle}>{t("lifetimeGiftSection")}</div>
            <div style={formGridStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="lifetimegifted">{t("lifetimeGiftedLabel")}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="lifetimegifted"
                  value={inputs.lifetimegifted}
                  onChange={(e) => handleInputChange('lifetimegifted', e.target.value)}
                  min="0"
                  step="0.01"
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
            </div>

            <div style={buttonGroupStyle}>
              <button
                type="submit"
                style={calculateBtnStyle}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t("calculateButton")}
              </button>
              <button
                type="button"
                style={clearBtnStyle}
                onClick={handleClear}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                {t("clearButton")}
              </button>
            </div>
          </form>

          {results && (
            <div style={resultsStyle} id="results-section">
              <h3 style={resultsH3Style}>{t("resultsTitle")}</h3>

              <div style={resultGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("grossEstateLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.grossEstate)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("totalDeductionsLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.totalDeductions)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("adjustedEstateLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.adjustedGrossEstate)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("lifetimeGiftsLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.lifetimegifted)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("taxableEstateLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.taxableEstate)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("exemptionLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.exemption)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("taxableAmountLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.taxableAmount)}</span>
                </div>
                <div style={resultHighlightStyle}>
                  <span style={resultLabelStyle}>{t("estateTaxLabel")}</span>
                  <span style={resultHighlightValueStyle}>{formatCurrency(results.estateTax)}</span>
                </div>
              </div>

              {results.taxableAmount > 0 && (
                <div style={taxTableStyle}>
                  <h4 style={taxTableH4Style}>{t("breakdownTitle")}</h4>
                  <div style={taxBreakdownStyle}>
                    <div style={taxRowStyle}>
                      <span>{t("taxableEstateLabel")}</span>
                      <span>{formatCurrency(results.taxableEstate)}</span>
                    </div>
                    <div style={taxRowStyle}>
                      <span>{t("lessExemptionLabel")}</span>
                      <span>-{formatCurrency(results.exemption)}</span>
                    </div>
                    <div style={taxRowStyle}>
                      <span>{t("taxableAmountLabel")}</span>
                      <span>{formatCurrency(results.taxableAmount)}</span>
                    </div>
                    <div style={taxRowStyle}>
                      <span>{t("taxRateLabel")}</span>
                      <span>40%</span>
                    </div>
                    <div style={taxRowHighlightStyle}>
                      <span>{t("estateTaxDueLabel")}</span>
                      <span>{formatCurrency(results.estateTax)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={infoBannerStyle}>
            <strong>{t("noteTitle")}</strong> {t("noteText")}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToStep1")}</li>
          <li style={listItemStyle}>{t("howToStep2")}</li>
          <li style={listItemStyle}>{t("howToStep3")}</li>
          <li style={listItemStyle}>{t("howToStep4")}</li>
          <li style={listItemStyle}>{t("howToStep5")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph2")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph3")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph4")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem' }}>{t("detailsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem' }}>{t("detailsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph2")}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem' }}>{t("detailsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph3")}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem' }}>{t("detailsSubtitle4")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph4")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion1")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer1")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion2")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer2")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion3")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer3")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion4")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer4")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion5")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer5")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <div style={referenceStyle}>
          <a href="https://www.corebridgefinancial.com/rs/home/financial-education/education-center/tools-and-calculators/estate-tax-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Estate Tax Planning Calculator - Corebridge Financial
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://smartasset.com/taxes/all-about-the-estate-tax" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            A Guide to the Federal Estate Tax for 2025 - SmartAsset.com
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.irs.gov/businesses/small-businesses-self-employed/estate-tax" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Estate tax | Internal Revenue Service
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.nerdwallet.com/taxes/learn/estate-tax" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Estate Tax: Definition, Rates and Who Pays in 2025 and 2026
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.calculator.net/estate-tax-calculator.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Estate Tax Calculator - Calculator.net
          </a>
        </div>
      </section>
    </div>
  );
}